import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import CanonicalTag from "@/components/CanonicalTag";
import gameRoomImage from "@/assets/game-room-media-adu.jpg";

const HowToUseADUGameRoomMediaRoom = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-game-room-media-room" />
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Game Room or Media Den
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Create the Ultimate Backyard Entertainment Hub
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
            Design a dedicated gaming and media ADU equipped for console gaming, movie nights, streaming, and social hangouts.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-[#E4683B] hover:bg-[#E4683B]/90 text-white px-8 py-3 text-lg"
            >
              📅 Book & Schedule
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3 text-lg"
              onClick={() => window.location.href = '/landing'}
            >
              📐 What fits my lot?
            </Button>
          </div>
        </div>

        {/* Main Image */}
        <div className="mb-16">
          <img 
            src={gameRoomImage} 
            alt="Ultimate backyard ADU game room and media den" 
            className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
          />
        </div>

        {/* Key Features & Benefits Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Key Features & Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Large screen or projector setup</h3>
              <p className="text-muted-foreground">Create an immersive viewing experience with professional-grade displays.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Surround sound & blackout shades</h3>
              <p className="text-muted-foreground">Full audio immersion with light control for the perfect atmosphere.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Comfortable lounge seating</h3>
              <p className="text-muted-foreground">Premium seating designed for hours of entertainment comfort.</p>
            </div>
          </div>
        </div>

        {/* Mid-Section CTAs */}
        <div className="text-center mb-16">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            variant="outline" 
            className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3"
            onClick={() => window.location.href = '/landing'}
          >
            📐 What fits my lot?
          </Button>
          <Button 
            className="bg-white text-[#E4683B] border border-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
            onClick={() => window.location.href = '/adu-calculator'}
          >
            💰 Get Price & ROI Report
          </Button>
        </div>
        </div>

        {/* Optimize for Accessibility Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Optimize for Accessibility
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              Ensure enough space for movement, thoughtful lighting, and quiet insulation for distraction-free gaming.
            </p>
          </div>
        </div>

        {/* Boost Home Value Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Boost Home Value & Leisure Lifestyle
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              A high-tech media den adds lifestyle appeal and resale value—perfect for gamers, families, or entertainment enthusiasts.
            </p>
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/our-work', '_blank')}
            >
              🏡 Our Portfolio
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/faq', '_blank')}
            >
              ❓ Our FAQ
            </Button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl max-h-[80vh] p-0">
          <div className="relative w-full h-[600px]">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              ✕
            </button>
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUGameRoomMediaRoom;